import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ORDER = {
  starter: 0,
  growth: 1,
  pro: 2,
};

function normalizePlan(plan) {
  const value = String(plan || "starter").trim().toLowerCase();
  if (value === "free") return "starter";
  return value;
}

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    tagline: "Explore the studio.",
    pricing: {
      monthly: { amount: "£0", cadence: "Free forever", planId: null },
      annual: { amount: "£0", cadence: "Free forever", planId: null },
    },
    features: [
      "SEO AI + Business Starter Kit",
      "50 generations / mo",
      "7-day history",
      "1 website / project",
      "Free Marketing Audit",
      "Copy and download results",
    ],
    featured: false,
    cta: "Start free",
  },
  {
    key: "growth",
    name: "Growth",
    tagline: "For founders creating better marketing faster.",
    pricing: {
      monthly: { amount: "£29", cadence: "per month", planId: "growth_monthly" },
      annual: { amount: "£290", cadence: "per year · save £58", planId: "growth_annual" },
    },
    features: [
      "All 10 AI tools unlocked",
      "SEO, Ads, Social, Reviews, Local, Brand",
      "Business Starter, Formation, Growth, Relate, Go-to-Source",
      "2,000 generations / mo",
      "Up to 3 websites / projects",
      "Unlimited private history",
      "Export to PDF / CSV",
      "Campaign reports",
      "Email support",
      "No API access",
    ],
    featured: true,
    cta: "Upgrade",
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "For teams and agencies running marketing operations.",
    pricing: {
      monthly: { amount: "£99", cadence: "per month", planId: "pro_monthly" },
      annual: { amount: "£990", cadence: "per year · save £198", planId: "pro_annual" },
    },
    features: [
      "Everything in Growth",
      "Unlimited AI generations",
      "Up to 10 websites / projects",
      "Workflow automation",
      "Approval workflows",
      "Action task board",
      "LinkedIn profile publishing",
      "LinkedIn Pages publishing once approved",
      "WordPress/blog draft publishing",
      "Newsletter/email export",
      "3 team seats",
      "API access · 3 active keys",
      "Priority support",
      "Dedicated onboarding",
    ],
    featured: false,
    cta: "Upgrade",
  },
];

const ADD_ONS = {
  starter: [
    { key: "addon_starter_50", label: "50 extra generations", price: "£5", description: "Best for quick testing." },
    { key: "addon_starter_100", label: "100 extra generations", price: "£8", description: "Good for a bigger content sprint." },
    { key: "addon_starter_200", label: "200 extra generations", price: "£15", description: "Best value for free users." },
  ],
  growth: [
    { key: "addon_growth_500", label: "500 extra generations", price: "£20", description: "Small campaign boost." },
    { key: "addon_growth_1000", label: "1,000 extra generations", price: "£23", description: "Best value for busy months." },
    { key: "addon_growth_2000", label: "2,000 extra generations", price: "£29", description: "Double your Growth allowance." },
  ],
};

const ACCOUNT_SLOT_ADDONS = [
  {
    key: "addon_accounts_plus_1",
    label: "+1 LinkedIn profile/page + 1 WordPress account",
    price: "£5",
    cadence: "per month",
    description: "Adds one extra publishing slot for each platform after checkout is enabled.",
  },
  {
    key: "addon_accounts_plus_2",
    label: "+2 LinkedIn profile/page + 2 WordPress accounts",
    price: "£8",
    cadence: "per month",
    description: "For users managing multiple brands or client sites.",
  },
  {
    key: "addon_accounts_plus_3",
    label: "+3 LinkedIn profile/page + 3 WordPress accounts",
    price: "£10",
    cadence: "per month",
    description: "Best value for small agencies and operators.",
  },
];

function BillingConfirmModal({ confirm, loading, onClose, onConfirm }) {
  if (!confirm) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="w-full max-w-md bg-white border border-[#E5E7EB] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-5 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="h-10 w-10 bg-[#EFF6FF] text-[#002FA7] flex items-center justify-center shrink-0">
            <WarningCircle size={22} weight="duotone" />
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
              Confirm plan change
            </p>
            <h3 className="font-display text-2xl font-black mt-1">{confirm.title}</h3>
          </div>
        </div>

        <div className="px-5 py-5">
          <p className="text-sm text-[#525252] leading-relaxed whitespace-pre-line">
            {confirm.message}
          </p>
        </div>

        <div className="px-5 py-4 border-t border-[#E5E7EB] flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Keep my plan
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="bg-[#0A0A0A] text-white px-4 py-2 text-sm font-semibold hover:bg-black disabled:opacity-50"
          >
            {loading ? "Opening…" : confirm.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, usage, refreshUsage } = useAuth();
  const [interval, setInterval] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const currentPlan = normalizePlan(usage?.plan || user?.plan || "starter");
  const addOns = ADD_ONS[currentPlan] || [];
  const isPaid = currentPlan === "growth" || currentPlan === "pro";

  const openBillingPortal = async () => {
    if (!user) {
      window.location.href = "/login";
      return;
    }

    setPortalLoading(true);

    try {
      const { data } = await axios.post(`${API}/billing/portal`, {
        return_url: window.location.href,
      });

      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(typeof detail === "string" ? detail : "Couldn't open billing portal.");
      setPortalLoading(false);
    }
  };

  const startCheckout = async (planId, loadingKey) => {
    if (!user) {
      toast.message("Sign in first to continue.");
      window.location.href = "/login";
      return;
    }

    setLoading(loadingKey);

    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });

      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Could not start checkout");
      setLoading(null);
    }
  };

  const handlePlanClick = async (plan) => {
    const targetPlan = plan.key;
    const tier = plan.pricing[interval];

    if (!user) {
      window.location.href = "/login";
      return;
    }

    if (targetPlan === currentPlan) return;

    if (targetPlan === "starter" && isPaid) {
      setConfirm({
        type: "portal",
        title: `Move from ${currentPlan} to Free?`,
        message:
          `This will take you to Stripe to cancel your ${currentPlan} subscription.\n\nRecommended setup: keep your paid access until the end of the current billing period, then move the account to Free through the webhook.`,
        confirmLabel: "Open billing portal",
      });
      return;
    }

    if (currentPlan === "pro" && targetPlan === "growth") {
      setConfirm({
        type: "portal",
        title: "Downgrade from Pro to Growth?",
        message:
          "This will take you to Stripe to manage your subscription.\n\nRecommended setup: keep Pro features until the end of the current billing period, then switch the next invoice to Growth at £29/month.",
        confirmLabel: "Open billing portal",
      });
      return;
    }

    if (!tier.planId) return;

    await startCheckout(tier.planId, targetPlan);
  };

  const handleAddonCheckout = async (addon) => {
    await startCheckout(addon.key, addon.key);
  };

  const handleAccountSlotAddonClick = (addon) => {
    toast.message(`${addon.label} will be enabled after the account add-on checkout step.`);
  };

  const confirmPortalAction = async () => {
    setConfirm(null);
    await openBillingPortal();
    if (refreshUsage) refreshUsage();
  };

  const planButtonLabel = (plan) => {
    if (currentPlan === plan.key) {
      if (plan.key === "starter") return "Current free plan";
      return "Current plan";
    }

    if (plan.key === "starter" && isPaid) return "Move to Free";
    if (currentPlan === "pro" && plan.key === "growth") return "Downgrade";
    if (PLAN_ORDER[plan.key] > PLAN_ORDER[currentPlan]) return "Upgrade";

    return plan.cta;
  };

  return (
    <div>
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-12 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-4">
            Pricing
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter">
            Simple pricing. No surprises.
          </h1>
          <p className="mt-4 sm:mt-5 max-w-xl text-[#525252] text-base sm:text-lg">
            Start free. Upgrade when your campaigns demand more.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="inline-flex border border-[#0A0A0A]" data-testid="interval-toggle">
              <button
                onClick={() => setInterval("monthly")}
                className={`px-5 py-2 font-bold text-sm ${interval === "monthly" ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A]"}`}
                data-testid="interval-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval("annual")}
                className={`px-5 py-2 font-bold text-sm inline-flex items-center gap-2 ${interval === "annual" ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A]"}`}
                data-testid="interval-annual"
              >
                Annual
                <span className={`font-mono text-[10px] px-1.5 py-0.5 ${interval === "annual" ? "bg-[#002FA7]" : "bg-[#002FA7] text-white"}`}>
                  −17%
                </span>
              </button>
            </div>

            {isPaid && (
              <>
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  className="btn-secondary"
                  data-testid="manage-subscription-btn"
                >
                  {portalLoading ? "Opening…" : "Manage subscription"}
                </button>
                <button
                  onClick={() => setConfirm({
                    type: "portal",
                    title: "Cancel subscription?",
                    message: "This opens Stripe so you can cancel safely. Recommended setup: keep paid access until the end of the current billing period, then your account moves to Free automatically when Stripe sends the cancellation webhook.",
                    confirmLabel: "Open Stripe billing",
                  })}
                  disabled={portalLoading}
                  className="border border-[#DC2626] text-[#DC2626] px-4 py-3 text-sm font-bold hover:bg-[#FEF2F2] disabled:opacity-50"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel subscription
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="container-wide py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {PLANS.map((p) => {
            const tier = p.pricing[interval];
            const isCurrent = currentPlan === p.key;
            const buttonLabel = loading === p.key ? "Redirecting…" : planButtonLabel(p);
            const isDowngrade = !isCurrent && PLAN_ORDER[p.key] < PLAN_ORDER[currentPlan];

            return (
              <div
                key={p.key}
                className={`relative bg-white border p-6 sm:p-8 flex flex-col gap-5 ${
                  p.featured ? "tracing-border border-[#002FA7]" : "border-[#E5E7EB]"
                }`}
                data-testid={`plan-${p.key}`}
              >
                {p.featured && !isCurrent && (
                  <span className="absolute -top-3 left-8 bg-[#002FA7] text-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em]">
                    Most popular
                  </span>
                )}
                {isCurrent && (
                  <span className="absolute -top-3 left-8 bg-[#0A0A0A] text-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid={`current-plan-badge-${p.key}`}>
                    Your plan
                  </span>
                )}
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                    {p.name}
                  </p>
                  <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-4xl sm:text-5xl font-black">{tier.amount}</span>
                    <span className="text-sm text-[#737373]">{tier.cadence}</span>
                  </div>
                  <p className="mt-2 text-[#525252]">{p.tagline}</p>
                </div>
                <ul className="space-y-3 border-t border-[#E5E7EB] pt-5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={18} weight="bold" className="text-[#002FA7] shrink-0 mt-[1px]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => !isCurrent && handlePlanClick(p)}
                  disabled={loading === p.key || isCurrent}
                  className={`${
                    isCurrent
                      ? "btn-secondary cursor-default opacity-70"
                      : isDowngrade
                        ? "btn-secondary"
                        : (p.featured ? "btn-primary" : "btn-secondary")
                  } w-full justify-center`}
                  data-testid={`checkout-btn-${p.key}`}
                >
                  {isCurrent ? <Check size={16} weight="bold" /> : null}
                  {buttonLabel}
                  {!isCurrent && <ArrowRight size={16} weight="bold" />}
                </button>
              </div>
            );
          })}
        </div>

        {addOns.length > 0 && (
          <div className="mt-12 border border-[#E5E7EB] bg-white p-6 sm:p-8" data-testid="generation-addons">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7] mb-2">
                  One-off add-ons
                </p>
                <h2 className="font-display text-3xl font-black tracking-tight">
                  Need more generations this month?
                </h2>
                <p className="mt-2 text-sm text-[#525252] max-w-2xl">
                  Buy extra generation credits without changing plan. Pro users do not need add-ons because Pro includes unlimited generations.
                </p>
              </div>
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#737373]">
                30-day credit window recommended
              </span>
            </div>

            {usage && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="addon-credit-summary">
                <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Plan remaining</p>
                  <p className="font-display text-2xl font-black mt-1">{usage.remaining ?? 0}</p>
                </div>
                <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1D4ED8]">Add-on credits</p>
                  <p className="font-display text-2xl font-black mt-1 text-[#002FA7]">{usage.addon_credits?.remaining ?? 0}</p>
                </div>
                <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Total available</p>
                  <p className="font-display text-2xl font-black mt-1">{usage.total_available ?? 0}</p>
                </div>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {addOns.map((addon) => (
                <div key={addon.key} className="border border-[#E5E7EB] p-5 bg-[#F5F5F5]">
                  <p className="font-semibold text-[#0A0A0A]">{addon.label}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-3xl font-black">{addon.price}</span>
                    <span className="text-xs text-[#737373]">one-off</span>
                  </div>
                  <p className="mt-2 text-xs text-[#737373]">{addon.description}</p>
                  <button
                    onClick={() => handleAddonCheckout(addon)}
                    disabled={loading === addon.key}
                    className="btn-secondary w-full justify-center mt-5"
                    data-testid={`addon-${addon.key}`}
                  >
                    {loading === addon.key ? "Redirecting…" : "Buy add-on"}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentPlan === "pro" && (
          <div className="mt-12 border border-[#E5E7EB] bg-white p-6 sm:p-8" data-testid="generation-addons-pro-note">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7] mb-2">
              One-off add-ons
            </p>
            <h2 className="font-display text-3xl font-black tracking-tight">
              Add-ons are only needed on Starter and Growth.
            </h2>
            <p className="mt-2 text-sm text-[#525252] max-w-2xl">
              Your Pro plan includes unlimited generations, so generation packs are hidden for this account.
              To test the add-on buttons, switch a test account to Starter or Growth.
            </p>
          </div>
        )}

        {currentPlan === "pro" && (
          <div className="mt-12 border border-[#E5E7EB] bg-white p-6 sm:p-8" data-testid="publishing-account-addons">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#002FA7] mb-2">
                Publishing account add-ons
              </p>
              <h2 className="font-display text-3xl font-black tracking-tight">
                Need more LinkedIn or WordPress accounts?
              </h2>
              <p className="mt-2 text-sm text-[#525252] max-w-2xl">
                Pro includes 3 LinkedIn profile/page accounts and 3 WordPress sites by default. These monthly add-ons extend that limit once account add-on checkout is enabled.
              </p>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#737373]">
              Cancel anytime · no refund after active use
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {ACCOUNT_SLOT_ADDONS.map((addon) => (
              <div key={addon.key} className="border border-[#E5E7EB] p-5 bg-[#F5F5F5]">
                <p className="font-semibold text-[#0A0A0A]">{addon.label}</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-black">{addon.price}</span>
                  <span className="text-xs text-[#737373]">{addon.cadence}</span>
                </div>
                <p className="mt-2 text-xs text-[#737373]">{addon.description}</p>
                <button
                  onClick={() => handleAccountSlotAddonClick(addon)}
                  className="btn-secondary w-full justify-center mt-5"
                  data-testid={`account-slot-addon-${addon.key}`}
                >
                  Coming soon
                  <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
          </div>
        )}

        <p className="mt-8 text-xs text-[#737373] font-mono uppercase tracking-[0.18em]">
          Secure checkout powered by Stripe · Cards accepted · Cancel anytime
        </p>
      </section>

      <BillingConfirmModal
        confirm={confirm}
        loading={portalLoading}
        onClose={() => setConfirm(null)}
        onConfirm={confirmPortalAction}
      />
    </div>
  );
}
